import Chart from 'react-apexcharts';
import React from 'react';
import { ApexOptions } from 'apexcharts';
import { parseJSONCleaned } from 'utilities/index';
import NodeWithCodePanelWrapper from './NodeWithCodePanelWrapper';
import { useDebounce } from 'hooks/common';
import { Box } from '@mui/material';
import { useSparkOutputReferences } from 'hooks/SparkBuilder/sparkStepReferences';
import { useParams } from 'react-router-dom';

type GraphOptionsType = {
  options: ApexOptions;
  series: ApexAxisChartSeries;
  type: Chart['props']['type'];
};

type GraphNodeProps = {
  readOnly?: boolean;
  initialContent: string | undefined;
  onChange?: (output: any) => Promise<void>;
  onError?: (errorMessage: string) => void;
  width?: number;
  minWidth?: string;
};

function GraphNode({
  initialContent = '',
  onChange,
  readOnly,
  onError,
  width,
  minWidth = 'unset',
}: GraphNodeProps) {
  const { id: sparkID = null } = useParams();
  const { replaceReferenceWithResolvedValue, isLoading: isReferencesLoading } =
    useSparkOutputReferences(sparkID || '');
  console.log('initialContent', initialContent);
  const [jsonString, setJsonString] = React.useState<string>(initialContent);
  const debouncedOnChange = useDebounce()(onChange ? onChange : () => {}, 500);
  const [options, setOptions] = React.useState<ApexOptions>();
  const [series, setSeries] = React.useState<ApexAxisChartSeries>();

  React.useEffect(() => {
    const parsed = parseJSONCleaned<GraphOptionsType>(jsonString);
    if (!parsed) {
      onError && onError('Error rendering chart');
      return;
    }
    const { options, series } = parsed;
    if (!options || !series.length) {
      console.log('Error rendering chart');
      onError && onError('Error rendering chart');
      setOptions(undefined);
      setSeries(undefined);
      return;
    }

    const updatedOptions = {
      ...options,
      chart: {
        ...(options.chart || {}),
        zoom: {
          enabled: false,
        },
        toolbar: {
          ...(options.chart?.toolbar || {}),
          tools: {
            ...(options.chart?.toolbar?.tools || {}),
            zoom: false,
            zoomin: false,
            zoomout: false,
          },
        },
      },
    };

    setOptions(updatedOptions);
    setSeries(series);
  }, [jsonString, onError]);

  const handleChange = (value: string) => {
    setJsonString(replaceReferenceWithResolvedValue(value));
    debouncedOnChange(value);
  };

  if (readOnly) {
    return (
      <Box
        sx={{
          '& .apexcharts-canvas': {
            minWidth,
            width: width && width * 0.95,
          },
          '& .apexcharts-toolbar': { display: 'none' },
        }}
        display='flex'
        justifyContent='center'
        alignItems='center'
        bgcolor={(theme) => theme.palette.common.white}
      >
        {options && series && (
          <Chart
            options={options || []}
            series={series || []}
            width='100%'
            maxHeight='20rem'
            minHeight='10rem'
            minWidth={600}
          />
        )}
      </Box>
    );
  }

  return (
    <NodeWithCodePanelWrapper
      onChange={handleChange}
      readOnly={readOnly}
      initialContent={parseJSONCleaned(jsonString) || ''}
      language='json'
    >
      <Box
        sx={{
          '& .apexcharts-canvas': { width: width && width * 0.95, minWidth },
          '& .apexcharts-toolbar': { display: 'none' },
          height: '100%',
        }}
      >
        {options && series && (
          <Chart
            options={options || []}
            series={series || []}
            width='100%'
            maxHeight='20rem'
            minHeight='10rem'
          />
        )}
      </Box>
    </NodeWithCodePanelWrapper>
  );
}

export default GraphNode;
